// FILECONTENT_START src/common/components/Form/useForm.ts
import api from "app/api";
import { useAppSelector } from "app/hooks";
import { AxiosRequestConfig } from "axios";
import { selectProfile } from "features/profile/profileSlice";
import I18n from "i18n-js";
import { get, pick } from "lodash";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Options } from "../../modules/helpers";
import Validator, { Rules, ValidationErrors } from "../../modules/validator";
import { globalMessageHandler } from "./../GlobalMessaging";

export interface UseFormReturnType<TItem, TOptions = Options, TId = any> {
  id?: TId;
  isCreate: boolean;
  isUpdate: boolean;
  item: TItem;
  originalValues: TItem;
  options: TOptions;
  errors: ValidationErrors;
  onChange: (newItem: TItem, id?: string, option?: any) => void;
  save: (options?: FormSaveOptions<Partial<TItem>>) => Promise<void>;
  loading: boolean;
  saving: boolean;
  validate: (fields?: string[]) => boolean;
  clear: () => void;
  addRules: (conditionalRules: Rules) => void;
  setSaving: (saving: boolean) => void;
  setErrors: (errors: ValidationErrors) => void;
  setItem: (item: TItem) => void;
  //Merge given object to the item state and ignore
  setAttributes: (attributes: Partial<TItem>, ignoreDirty?: boolean) => void;
  load: (silent?: boolean) => Promise<void>;
  deleteItem: (silent?: boolean) => Promise<SaveFormProps<TItem> | void>;
  isDirty: boolean;
  getIsDirty: (id?: string) => boolean;
  loadOptions?: (
    requestConfig?: AxiosRequestConfig,
    component?: string
  ) => Promise<TOptions>;
  isView: boolean;
  requiredFields: string[];
  getRequiredFields: (key?: string) => string[];
  toggleViewMode: Function;
}

export type FormRefType<
  TItem,
  TOptions = Options,
  TId = number
> = MutableRefObject<UseFormReturnType<TItem, TOptions, TId> | undefined>;

export interface UseFormProps<TItem, TOptions = Options, TId = any> {
  /** The primary key of item. If there is an id update will be fired on save anyway create */
  id?: TId;
  /** Default values of the form */
  defaultValues?: Partial<TItem>;
  /** Load function to get data from server */
  loadItem?: (id: TId) => Promise<TItem>;
  /** Load function to get data from server */
  loadOptions?: (
    requestConfig?: AxiosRequestConfig,
    component?: string
  ) => Promise<TOptions>;
  /** Update function to update data from server */
  updateItem?: (id: TId, item: TItem) => Promise<SaveFormProps<TItem>>;
  /** Create function to insert data from server */
  createItem?: (item: TItem) => Promise<SaveFormProps<TItem>>;
  deleteItem?: (
    id: any,
    onlyTrashed?: boolean
  ) => Promise<SaveFormProps<TItem>>;
  /** Joi rules object to validate form data on client side before save. */
  rules?: Rules;
  /** Using I18n-js Category.attributes to use as validation labels for fields. label name will get this ways: I18n.t("translationCategory.attributes") */
  translationCategory?: string;
  /** Function fired after succes saved if client and server validation is passed */
  afterSave?: (newItem: TItem, isCreate: boolean) => void;
  afterDelete?: (deletedItem: TItem) => void;
  afterSaveMessage?: (isCreate?: boolean) => string | null | undefined;
  /** Function fired after succes load */
  afterLoad?: (newItem: TItem) => void;
  /** Function fired after form data changed (triggered by onChange) */
  afterChange?: (newItem: TItem, id?: string, option?: any) => void;
  /** Function fired after form data is invalid */
  onValidationFailed?: (newItem: TItem, error?: any) => void;
  customValidation?: (newItem: TItem) => any;
  formRef?: FormRefType<TItem, TOptions, TId>;
  /** Show leave confirm modal if form is dirty */
  showLeaveConfirm?: boolean;
  /** turn isView to true on start */
  viewByDefault?: boolean;
  /** Dont replace last url part to id. Example replace users/create to users/12 after create */
  ignoreNavigateAfterCreate?: boolean;
  /** Dont replace last url part to id. Example replace users/create to users/12 after create */
  ignoreSetItemAfterSave?: boolean;
  /** Dont show success notification after create and save */
  ignoreSuccessNotification?: boolean;
  defaultValuesMakeDirty?: boolean;
  confirmAttribute?: string;
  registerObjectTouch?: string;
}

export type FormSaveOptions<T = any> = {
  /** Values to merge with item */
  withValues?: T;
  disableLoader?: boolean;
  addNewAfterCreate?: boolean;
};

export interface SaveFormProps<TItem> {
  success?: boolean;
  data?: TItem;
  error?: any;
}

export default function useForm<TItem, TOptions = Options, TId = any>({
  id: _id,
  defaultValues,
  loadItem,
  updateItem,
  createItem,
  deleteItem,
  loadOptions,
  rules: permanentRules,
  translationCategory,
  afterSave,
  afterDelete,
  afterLoad,
  afterChange,
  onValidationFailed,
  afterSaveMessage,
  formRef,
  showLeaveConfirm,
  ignoreSetItemAfterSave,
  viewByDefault,
  customValidation,
  ignoreNavigateAfterCreate = false,
  ignoreSuccessNotification = false,
  defaultValuesMakeDirty,
  confirmAttribute,
  registerObjectTouch,
}: UseFormProps<TItem, TOptions, TId>) {
  const id: any = _id && String(_id) !== "create" ? _id : undefined;

  const [item, setItem] = useState<TItem>(
    (defaultValues as TItem) || ({} as TItem)
  );
  //const [initialized.current, setInitialized] = useState<boolean>(!!loadItem);
  const initialized = useRef<boolean>(
    loadOptions || (loadItem && id) ? false : true
  );
  const originalValues = useRef(
    defaultValuesMakeDirty ? ({} as TItem) : item || ({} as TItem)
  );
  const [options, setOptions] = useState<TOptions>({} as TOptions);
  const [isView, setIsView] = useState<boolean>(viewByDefault || false);
  const afterChangeRequest = useRef<{ id?: string; option?: any }>();
  const afterCreateRequest = useRef<boolean>(false);
  const afterUpdateRequest = useRef<boolean>(false);
  const afterLoadRequest = useRef<boolean>(false);
  const rules = useRef<Rules | undefined>(permanentRules);
  const useNav = !process.env.NEXT_PUBLIC_VERSION
    ? useNavigate
    : () => (a: string, b: any) => {};
  const navigate = useNav();
  rules.current = permanentRules;

  const isTouched = useRef(false);
  const profile = useAppSelector(selectProfile);

  // Function to register the object touch
  async function registerObjectTouchAsync(object: string, objectId: any) {
    const { success, data, error } = await api.put(
      `user/touch?object=${object}&objectId=${objectId}`
    );
    if (success) {
      //console.log("Touch registered successfully");
      isTouched.current = true; // Set isTouched to true once the touch is registered
    } else {
      console.error("Error registering touch:", error);
    }
  }

  // Function to check if the tab is active (Web)
  const isAppActive = () => !document.hidden;

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    // Ensure that registerObjectTouch is defined before registering listeners
    if (!!id && registerObjectTouch) {
      // Register touch immediately
      if (!isTouched.current) {
        registerObjectTouchAsync(registerObjectTouch, id);
      }

      const interval =
        //@ts-ignore
        profile?.touch_interval && profile.touch_interval > 0
          ? //@ts-ignore
            Number(profile.touch_interval)
          : 5;

      // Convert touch_interval from seconds to milliseconds
      const intervalMs = interval * 1000;

      // Set up the interval to repeatedly register touch only if the tab is active
      intervalId = setInterval(() => {
        if (isAppActive()) {
          registerObjectTouchAsync(registerObjectTouch, id);
        }
      }, intervalMs);

      // Listener for visibility change on the web
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log("Tab is active");
        } else {
          console.log("Tab is inactive");
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Clean up interval and visibility change listener on the web
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }
    //@ts-ignore
  }, [id, registerObjectTouch, profile.touch_interval]);

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState<boolean>(
    loadItem && id ? true : false
  );
  const [saving, setSaving] = useState<boolean>(false);

  function onChange(newItem: TItem, id?: string, option?: any) {
    setItem({ ...item, ...newItem });
    afterChangeRequest.current = { id, option };
  }

  function clear() {
    setItem({} as TItem);
    setErrors({});
  }

  function addRules(conditionalRules: Rules) {
    if (permanentRules) {
      rules.current = { ...permanentRules, ...conditionalRules };
    } else {
      rules.current = conditionalRules;
    }
  }

  function validate(fields?: string[]) {
    if (rules.current || customValidation) {
      let values: any = {};
      if (item) {
        Object.keys(item).forEach(element => {
          let i: any = item;
          if (i[element] !== undefined && i[element] !== null) {
            values[element] = i[element];
          }
        });
      }
      let errors: any = {};

      if (customValidation) {
        errors = { ...customValidation(values) };
      }

      if (rules.current) {
        const result = Validator.validate(
          fields ? pick(rules.current, fields) : rules.current,
          values,
          translationCategory || ""
        );
        if (!result.success) {
          errors = { ...errors, ...result.errors };
        }
      }
      setErrors(errors);
      if (Object.keys(errors).length > 0) {
        return false;
      }
    }
    return true;
  }

  async function update(options: FormSaveOptions<Partial<TItem>> = {}) {
    if (updateItem && id) {
      let _item = options.withValues
        ? { ...item, ...options.withValues }
        : item;
      setSaving(true);
      const { success, data, error } = await updateItem(id, _item);
      setSaving(false);
      if (success) {
        if (data) {
          const newItem = { ...item, ...data };
          //const newItem = { ..._item, ...data };
          originalValues.current = newItem;
          //afterUpdateRequest.current = true;
          if (ignoreSetItemAfterSave) {
            originalValues.current = item;
            setItem(item);
            afterSave && afterSave(item, false);
          } else {
            afterUpdateRequest.current = true;
            setItem(newItem);
          }
          return newItem;
        } else {
          originalValues.current = item as any;
          afterSave && afterSave(item, false);
          return item;
        }
      } else {
        if (error && Object.keys(error).length > 0) {
          //setErrors(transformFormErrors(error));
          setErrors(error);
          onValidationFailed && onValidationFailed(item, error);
        }
      }
    }
  }

  async function create(options: FormSaveOptions<Partial<TItem>> = {}) {
    if (createItem) {
      let _item = options.withValues
        ? { ...item, ...options.withValues }
        : item;
      setSaving(true);
      const { success, data, error } = await createItem(_item);
      setSaving(false);
      if (success) {
        if (data) {
          const newItem = { ...item, ...data };
          //const newItem = { ..._item, ...data };
          originalValues.current = newItem;
          //afterCreateRequest.current = true;
          if (ignoreSetItemAfterSave) {
            originalValues.current = item;
            setItem(item);
            afterSave && afterSave(item, true);
          } else {
            afterCreateRequest.current = true;
            setItem(newItem);
          }
          return newItem;
        } else {
          originalValues.current = item as any;
          afterSave && afterSave(item, true);
          return item;
        }
      } else {
        if (error && Object.keys(error).length > 0) {
          //setErrors(transformFormErrors(error));
          setErrors(error);
          onValidationFailed && onValidationFailed(item, error);
        }
      }
    }
  }

  async function save(options: FormSaveOptions<Partial<TItem>> = {}) {
    if (!validate()) {
      onValidationFailed && onValidationFailed(item);
      return;
    }
    let data: any;
    if (!options.disableLoader) {
      //loader.show();
    }

    if (id) {
      data = await update(options);
    } else {
      data = await create(options);
    }
    if (!options.disableLoader) {
      //loader.hide();
    }

    if (data) {
      if (!ignoreSuccessNotification) {
        if (afterSaveMessage) {
          const message = afterSaveMessage(!id);
          if (message) {
            globalMessageHandler.success(message);
          }
        } else {
          globalMessageHandler.success(
            I18n.t(id ? "App.successSaved" : "App.successCreated")
          );
        }
      }

      if (options?.addNewAfterCreate && !id) {
        setItem({} as TItem);
      } else if (!ignoreNavigateAfterCreate && !id && data.id) {
        //Replace last part of url with id. Example replace users/create to users/12 after create
        navigate(`../${String(data.id)}`, { replace: true });
      } else if (!ignoreNavigateAfterCreate && !id) {
        //Replace last part of url with id. Example replace users/create to users/12 after create
        navigate("..", { replace: true });
      }

      //afterSave && afterSave(data, !id);
    }
  }

  async function load(silent?: boolean) {
    //Load options and item paralel
    if (loadOptions && loadItem && id) {
      if (silent !== true) {
        setLoading(true);
      }
      const [options, data] = await Promise.all([loadOptions(), loadItem(id)]);
      initialized.current = true;
      if (silent !== true) {
        setLoading(false);
      }
      setOptions(options);
      if (data) {
        afterLoad && afterLoad(data);
        originalValues.current = data;
        afterLoadRequest.current = true;
        setItem(data);
        setErrors({});
      } else {
        afterLoad && afterLoad(data);
      }
      return;
    }

    if (loadOptions) {
      if (silent !== true) {
        setLoading(true);
      }
      const options: TOptions = await loadOptions();
      initialized.current = true;
      setOptions(options);
      if (!loadItem || !id) {
        if (silent !== true) {
          setLoading(false);
        }
      }
    }
    if (loadItem && id) {
      if (silent !== true) {
        setLoading(true);
      }
      const data = await loadItem(id);
      initialized.current = true;
      if (silent !== true) {
        setLoading(false);
      }
      if (data) {
        if (afterLoad) {
          afterLoad(data);
        }
        originalValues.current = data;
        setItem(data);
        setErrors({});
      }
    }
  }
  async function _delete(
    silent?: boolean
  ): Promise<SaveFormProps<TItem> | void> {
    if (deleteItem) {
      let attributeValue = (item as any).name || (item as any).id;
      if (confirmAttribute) {
        attributeValue = (item as any)[confirmAttribute];
      }

      globalMessageHandler.confirm(
        I18n.t("App.deleteItemConfirm", {
          item: `${attributeValue}`,
        }),
        async () => {
          if (!silent) {
            setLoading(true);
          }
          const response = await deleteItem(id);
          if (!silent) {
            setLoading(false);
          }
          if (response.success) {
            afterDelete && afterDelete(item);
            globalMessageHandler.success(I18n.t("App.successDeleted"));
          }
        }
      );
    }
  }

  function normalizeEmptyValue(value: any) {
    if (value === "") {
      return null;
    }
    if (value === undefined) {
      return null;
    }
    if (value === null) {
      return null;
    }
    if (value === 0) {
      return 0;
    }
    if (
      typeof value === "string" &&
      !isNaN(Number(value)) &&
      value === String(Number(value))
    ) {
      return Number(value);
    }
    return value;
  }

  function getIsDirty(id?: string): boolean {
    if (id) {
      let oldValue: any = normalizeEmptyValue(get(originalValues.current, id));
      let newValue: any = normalizeEmptyValue(get(item, id));
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    }
    let hasDirty = false;
    Object.keys(item as any).forEach((key: string) => {
      let oldValue: any = normalizeEmptyValue(get(originalValues.current, key));
      let newValue: any = normalizeEmptyValue((item as any)[key]);

      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        hasDirty = true;
      }
    });

    return hasDirty;
  }

  async function _loadOptions(
    requestConfig?: AxiosRequestConfig,
    component?: string
  ): Promise<TOptions> {
    if (loadOptions) {
      const options: TOptions = await loadOptions(requestConfig, component);
      setOptions(options);
      return options;
    }
    return {} as TOptions;
  }

  function setAttributes(attributes: Partial<TItem>, ignoreDirty?: boolean) {
    if (ignoreDirty) {
      originalValues.current = { ...originalValues.current, ...attributes };
    }
    setItem({ ...item, ...attributes });
  }

  const form: UseFormReturnType<TItem, TOptions, TId> = {
    id,
    isCreate: !id,
    isUpdate: !!id,
    item,
    options,
    errors,
    onChange,
    save,
    loading: !initialized.current || loading,
    saving,
    validate,
    clear,
    addRules,
    setSaving,
    setErrors,
    setItem,
    setAttributes,
    originalValues: originalValues.current,
    loadOptions: _loadOptions,
    load,
    isDirty: getIsDirty(),
    getIsDirty,
    isView,
    requiredFields: rules.current ? Object.keys(rules.current) : [],
    getRequiredFields: (key?: string) =>
      rules.current
        ? Object.keys(key ? get(rules.current, key, []) : rules.current)
        : [],
    toggleViewMode: () => setIsView(!isView),
    deleteItem: _delete,
  };

  useEffect(() => {
    if (formRef) {
      formRef.current = form;
    }

    if (_id && (loadItem || loadOptions)) {
      load();
    }
  }, [_id]);

  //Handle after Events
  useEffect(() => {
    if (afterChangeRequest.current) {
      const { id, option } = afterChangeRequest.current;

      afterChangeRequest.current = undefined;
      if (id && errors && errors[id]) {
        form.validate();
      }
      afterChange && afterChange(item, id, option);
    }
    if (afterUpdateRequest.current) {
      afterUpdateRequest.current = false;
      afterSave && afterSave(item, false);
    }
    if (afterCreateRequest.current) {
      afterCreateRequest.current = false;
      afterSave && afterSave(item, true);
    }
    if (afterLoadRequest.current) {
      afterLoadRequest.current = false;
      afterLoad && afterLoad(item);
    }
  }, [item]);

  /*useEffect(
    () =>
      navigation.addListener("beforeRemove", e => {
        if (!getIsDirty() || !showLeaveConfirm) {
          // If we don't have unsaved changes, then we don't need to do anything
          return;
        }
        // Prevent default behavior of leaving the screen
        e.preventDefault();

        // Prompt the user before leaving the screen
        globalMessageHandler.confirm(I18n.t("App.unsavedChangesLeave"), () => {
          navigation.dispatch(e.data.action);
        });
      }),
    [navigation, item, showLeaveConfirm]
  );*/

  return form;
}

// FILECONTENT_END src/common/components/Form/useForm.ts
